import React, { useState, useRef, useEffect } from "react";
import { useChatStore } from "@store";
import { SendIcon, MagicWandIcon } from "../common/Icons";
import { useAuthStore } from "@store";
import { logError, logDebug } from "../../utils/logging";
import { useGuidedFlowStore } from "@store";
import { improvePrompt } from "../../services/apiClientV2";
import styles from "./ChatInput.module.css";

export const ChatInput: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const [isImproving, setIsImproving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const sendMessage = useChatStore((state) => state.sendMessage);
  const stopStreaming = useChatStore((state) => state.stopStreaming);
  const isStreaming = useChatStore((state) => state.isStreaming);
  const generalError = useChatStore((state) => state.generalError);
  const streamError = useChatStore((state) => state.streamError);

  const isRateLimited = useAuthStore((state) => state.isRateLimited);
  const apiError = useAuthStore((state) => state.apiError);

  const { status: guidedFlowStatus, abandonFlow } = useGuidedFlowStore();

  const hasError = generalError || streamError || apiError;

  const messages = useChatStore((state) => state.messages);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.min(scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (guidedFlowStatus === "collecting") {
      abandonFlow();
    }
    setInputValue(e.target.value);
  };

  const submitMessage = async () => {
    const messageText = inputValue.trim();
    if (!messageText || isStreaming) {
      return;
    }

    try {
      const currentPageUrl = window.location.href;
      await sendMessage(messageText, currentPageUrl);
      setInputValue("");
    } catch (e) {
      logError("Error sending message from ChatInput:", e);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isStreaming) {
      stopStreaming();
      return;
    }
    await submitMessage();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitMessage();
    } else if (e.key === "ArrowUp" && !inputValue && messages.length > 0) {
      const lastUserMessage = [...messages].reverse().find(m => m.sender === 'user');
      if (lastUserMessage) {
        e.preventDefault();
        setInputValue(lastUserMessage.content);
      }
    }
  };

  const handleImprovePrompt = async () => {
    const promptText = inputValue.trim();
    if (!promptText || isImproving || isStreaming) {
      return;
    }

    setIsImproving(true);
    logDebug("[ChatInput] Improving prompt:", promptText.substring(0, 50) + "...");

    try {
      const result = await improvePrompt(promptText);
      if (result.improved_prompt) {
        setInputValue(result.improved_prompt);
        logDebug("[ChatInput] Prompt improved successfully");
      }
    } catch (e) {
      logError("Error improving prompt:", e);
      // Keep original text on error - user can still send it
    } finally {
      setIsImproving(false);
      // Focus back on textarea
      textareaRef.current?.focus();
    }
  };

  const isDisabled = (!inputValue.trim() || isRateLimited) && !isStreaming;
  const isMagicDisabled = !inputValue.trim() || isImproving || isStreaming || isRateLimited;

  return (
    <form onSubmit={handleSubmit} className={styles.chatInputContainer}>
      <div className={styles.inputWrapper}>
        <button
          type="button"
          onClick={handleImprovePrompt}
          disabled={isMagicDisabled}
          className={`${styles.magicButton} ${isImproving ? styles.improving : ''}`}
          aria-label="Îmbunătățește promptul"
          title="Îmbunătățește promptul cu AI"
        >
          <MagicWandIcon className={styles.magicIcon} title="Îmbunătățește" />
        </button>
        <textarea
          ref={textareaRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={
            hasError
              ? apiError || generalError || streamError || "A apărut o eroare"
              : "Scrie aici întrebarea sau nevoia ta..."
          }
          disabled={isRateLimited || (isStreaming && false)}
          readOnly={isStreaming}
          className={styles.inputField}
          aria-label="Câmp pentru mesaj chat"
          rows={1}
        />
        <button
          type="submit"
          disabled={isDisabled}
          className={`${styles.sendButton} ${isStreaming ? styles.stopButton : ''}`}
          aria-label={isStreaming ? "Oprește generarea" : "Trimite mesaj"}
          title={isStreaming ? "Oprește generarea" : "Trimite mesaj"}
        >
          {isStreaming ? (
            <div className={styles.stopIcon} />
          ) : (
            <SendIcon className="chat-input-send-icon" title="Trimite" />
          )}
        </button>
      </div>
    </form>
  );
};
